import { defaultReportTabState, isReportsTabDefaultState, variantIdState } from "atoms";
import { projectIdState } from "atoms/atoms-admin";
import { BrmMainTable } from "brm/tables/BrmTables";
import CustomTable from "components/elements/CustomTableDivStyled";
import systemModals from "components/modals/Modals";
import { COMMON, REPORT, TYPE } from "constants/brm";
import { EXPORT_TARGET, ExportTableModal, ExportWizard, useExportTable } from "features/exporter";
import { useModal } from "hooks/";
import * as React from "react";
import toast from "react-hot-toast";
import { useRecoilValue, useSetRecoilState } from "recoil";

import { createColumnActions } from "brm/tables/services/column/columnFactory";
import { DownloadButton, ErrorBanner, LoadingSpinner as Loading } from "components/elements";
import {
  ConfigurationApi,
  DETAIL_TABLE_TYPE,
  DetailTableType,
  ExportButton,
  ExportContext,
  LinkStyles,
  useProject,
} from "features/brm";
import { useExportTestGuidance } from "features/brm/api/component/variant/exportTestGuidance";
import { useRoles } from "features/brm/hooks/useRoles";
import { RoutePath } from "routes/route-paths";
import { dloadJSON, formattedTimeStamp } from "utils/report-utils";

interface ICallbackOpts {
  target: string;
  type: string;
}

export const ReportsTable = () => {
  const { isSystemEngineer, isRiskAnalyst } = useRoles();

  const [currentProject, { isActive: isProjectActive }] = useProject();
  const projectId = useRecoilValue(projectIdState);
  const variantId = useRecoilValue(variantIdState);
  const setIsReportsTabDefault = useSetRecoilState(isReportsTabDefaultState);
  const setDefaultReportTab = useSetRecoilState(defaultReportTabState);
  const [exportTraIsOpen, setExportTraIsOpen] = React.useState(false);
  const [exportRARIsOpen, setExportRARIsOpen] = React.useState(false);
  const [exportSCTMIsOpen, setExportSCTMIsOpen] = React.useState(false);
  const [allReportsData, setAllReportsData] = React.useState([{}]);

  const {
    disableExport: isTnEExportDisabled,
    setExportTableData,
    setFileNamePrefix,
    handleTableExport,
  } = useExportTable();
  const { isVisible: isTnEModalVisible, toggle: toggleTnEModal } = useModal();
  // The T&E button must be disabled as soon as the user clicks it, so disableExport from useExportTable cannot be used for this purpose.
  const disableTnEExportButton = React.useRef(false);
  const isTnEExportRequestSent = React.useRef<boolean>(false);
  const isTnEModalCanceled = React.useRef<boolean>(false);
  const exportTnEJsonData = React.useRef<any>(null);
  const exportTnEOptions = React.useRef<any>(null);

  const { data: systemData, isError: isSystemError } = ConfigurationApi.useSystems({
    projectId,
    options: { enabled: !!projectId && (isSystemEngineer || isRiskAnalyst) },
  });

  const toggleTnECloseModalEnableButton = React.useCallback(() => {
    isTnEModalCanceled.current = true;
    disableTnEExportButton.current = false;
    toggleTnEModal();
  }, [toggleTnEModal]);

  const toggleTnECloseModal = React.useCallback(() => {
    isTnEModalCanceled.current = true;
    toggleTnEModal();
  }, [toggleTnEModal]);

  /**
   * Download T&E report
   */
  const downloadTnEReport = React.useCallback(
    (opts: ICallbackOpts) => {
      if (opts.target === EXPORT_TARGET.json && exportTnEJsonData.current) {
        const fileName = "T&E";
        const jsonData = {
          metaData: {
            reportType: REPORT.tne,
            project: fileName,
            reportGenerationDate: exportTnEJsonData.current.timestamp,
            numOfRows: exportTnEJsonData.current.count,
          },
          rows: exportTnEJsonData.current.data,
        };
        dloadJSON(
          jsonData,
          `${currentProject?.name || "T&E"}_${DetailTableType.TEST_GUID_RPT.key}_${
            exportTnEJsonData.current.timestamp
          }.json`
        );
      }
      if (opts.target === EXPORT_TARGET.csv) {
        handleTableExport(opts);
      }
      setExportTableData(null);
    },
    [currentProject?.name, handleTableExport, setExportTableData]
  );

  const makePercentCell = (percent: number) => {
    if (percent) {
      // Percent is coming back as a value between 0-1 --> therefore multiply by 100
      const processedPercent = percent * 100;
      if (processedPercent >= 0.1) {
        return processedPercent.toFixed(1);
      }
      return 0.0;
    }
    return COMMON.hyphen;
  };

  const makeScoreCell = (score: number) => {
    if (score) {
      return score.toFixed(1);
    }
    return COMMON.hyphen;
  };

  const makeMitScoreCell = (mitigatedScore: any) => {
    if (mitigatedScore) {
      return mitigatedScore.toFixed(1);
    }
    if (mitigatedScore === 0) {
      return "0";
    }
    return COMMON.hyphen;
  };

  const { mutate: exportTestGuidance } = useExportTestGuidance({
    options: {
      onSuccess: (data) => {
        const dataWithTestcasesKeysSorted = {
          ...data,
          testcases: [
            {
              "R-Rank": "",
              Risk: "",
              "R-Category": "",
              "R-Objective": "",
              Asset: "",
              "R-Impact": "",
              "R-Likelihood": "",
              "R-Level": "",
              "R-Percent": "",
              "R-Score": "",
              "R-Mitigated Score": "",
              Attack: "",
              "A-Likelihood": "",
              "V-Rank": "",
              Vulnerability: "",
            },
          ],
        };

        if (data.testcases.length) {
          dataWithTestcasesKeysSorted.testcases = data.testcases.map(
            (testcase: {
              rank: string;
              risk: string;
              category: string;
              objective: string;
              asset: string;
              "risk impact": string;
              "risk likelihood": string;
              "risk level": string;
              percent: number;
              score: number;
              "mitigated score": string;
              attack: string;
              likelihood: string;
              vrank: string;
              vulnerability: string;
            }) => {
              return {
                "R-Rank": testcase.rank,
                Risk: testcase.risk,
                "R-Category": testcase.category,
                "R-Objective": testcase.objective,
                Asset: testcase.asset,
                "R-Impact": testcase["risk impact"],
                "R-Likelihood": testcase["risk likelihood"],
                "R-Level": testcase["risk level"],
                "R-Percent": makePercentCell(testcase.percent),
                "R-Score": makeScoreCell(testcase.score),
                "R-Mitigated Score": makeMitScoreCell(testcase["mitigated score"]),
                Attack: testcase.attack,
                "A-Likelihood": testcase.likelihood,
                "V-Rank": testcase.vrank,
                Vulnerability: testcase.vulnerability,
              };
            }
          );
        }

        // console.log(
        //   "exportTestGuidance data:",
        //   // typeof data.testcases[50]["mitigated score"],
        //   // typeof data.testcases[100].percent,
        //   dataWithTestcasesKeysSorted
        // );

        /* CSV Format */
        const timestamp = new Date(Date.now()).toISOString().replace(/T/, "_").replace(/\..+/, "").replace(/-|:/g, "");
        const isReport = true;
        setExportTableData(
          {
            originalName: `_${DetailTableType.TEST_GUID_RPT.key}_${timestamp}`,
            data: dataWithTestcasesKeysSorted.testcases,
          },
          isReport
        );

        /* JSON Format */
        exportTnEJsonData.current = {
          data: dataWithTestcasesKeysSorted,
          timestamp: formattedTimeStamp(),
          count: dataWithTestcasesKeysSorted.testcases.length,
        };

        disableTnEExportButton.current = false; // Request has finished. Enable the Export button.
      },
      onError: (err) => {
        toggleTnEModal();
        disableTnEExportButton.current = false;
        toast.error(`Error generating T&E report: ${err.message}`, {
          duration: 4000,
        });
      },
    },
  });

  React.useEffect(() => {
    if (!isTnEExportDisabled) {
      if (!isTnEModalCanceled.current) {
        downloadTnEReport(exportTnEOptions.current);
        toggleTnEModal(); // If the modal was canceled don't toggle it or it'll re-open
      }
      isTnEModalCanceled.current = false;
      isTnEExportRequestSent.current = false;
    }
  }, [isTnEExportDisabled, downloadTnEReport, setExportTableData, toggleTnEModal]);

  const handleExportTnEReport = (opts: ICallbackOpts) => {
    setFileNamePrefix(currentProject?.name || "T&E");
    exportTnEOptions.current = opts;
    disableTnEExportButton.current = true;
    isTnEExportRequestSent.current = true; // The request to the backend is sent below. If the modal is closed the Export button shouldn't be enabled with it.
    exportTestGuidance({ variantId });
    // downloadTnEReport(opts);
  };

  const createButton = React.useCallback(
    (cellProps) => {
      if (cellProps.cell.row.original.name === DetailTableType.TRA_RPT.name) {
        return <DownloadButton onClick={() => setExportTraIsOpen(true)} />;
      }

      if (cellProps.cell.row.original.name === DetailTableType.RAR_RPT.name) {
        return <DownloadButton onClick={() => setExportRARIsOpen(true)} />;
      }

      if (cellProps.cell.row.original.name === DetailTableType.SCTM_COMPL_RPT.name) {
        return <DownloadButton onClick={() => setExportSCTMIsOpen(true)} />;
      }

      if (cellProps.cell.row.original.name === DetailTableType.TEST_GUID_RPT.name) {
        return (
          <ExportButton onClick={toggleTnEModal} disableExport={disableTnEExportButton.current || !isProjectActive} />
        );
      }

      return COMMON.na;
    },
    [isProjectActive, toggleTnEModal]
  );

  const columns = React.useMemo(
    () => [
      {
        Header: "Name",
        accessor: "name",
        disableSortBy: true,
        Cell: (cellProps: {
          cell: {
            row: {
              original: {
                name: string;
                path: string;
                key: string;
              };
            };
          };
        }) => {
          const {
            cell: {
              row: {
                original: { name, path, key },
              },
            },
          } = cellProps;

          if (path) {
            return (
              <LinkStyles.CleanLink
                id={`hrefTo_${path}`}
                to={path || "#"}
                onClick={() => {
                  setIsReportsTabDefault(true);
                  setDefaultReportTab(key);
                }}
              >
                {name}
              </LinkStyles.CleanLink>
            );
          }

          return <p>{name}</p>;
        },
      },
      createColumnActions(createButton, { disableFilters: true }),
    ],
    [createButton, setDefaultReportTab, setIsReportsTabDefault]
  );

  React.useEffect(() => {
    function getData() {
      const systemId = systemData?.id;

      const factsReports = [
        // DetailTableType.FACT_ANALYSIS_INFORMATION_LEVEL_RPT,  // TODO : needs to be enabled after March release
        DetailTableType.FACT_ANALYSIS_INVENTORY_RPT,
        DetailTableType.FACT_ANALYSIS_RPT,
      ];

      const cntrlsReports = [
        DetailTableType.VARIANT_DIFF_RPT,
        DetailTableType.CNTRL_GUINDANCE_RPT,
        DetailTableType.CNTRL_RPT,
        DetailTableType.CSA_KPP_RPT,
        DetailTableType.CSF_RPT,
        DetailTableType.SCTM_COMPL_RPT,
        DetailTableType.TEST_GUID_RPT,
      ];

      const projectReports = [
        { ...DetailTableType.SYSTEM_CWE_RPT, path: RoutePath.Project.replace(":id", projectId) },
        { ...DetailTableType.CWE_RPT, path: RoutePath.Project.replace(":id", projectId) },
        {
          ...DetailTableType.TRA_RPT_TMP,
          path: RoutePath.Project.replace(":id", projectId).concat("?isTraImport=1"),
        },
        { ...DetailTableType.TRA_RPT, path: "" },
        { ...DetailTableType.RAR_RPT, path: "" },
      ];

      // const noPathReports = [{ ...DetailTableType.RAR_RPT, path: "" }];

      const sysEnggReports = factsReports.map((e) => ({ ...e, path: RoutePath.System.replace(":id", systemId) }));

      const riskAnalystReports = sysEnggReports.concat(projectReports);

      const riskAnalystWithVariantReports = cntrlsReports
        .map((e) => ({
          ...e,
          path:
            e.key === DETAIL_TABLE_TYPE.sctm_compl_rpt.key || e.key === DETAIL_TABLE_TYPE.test_guid_rpt.key
              ? ""
              : RoutePath.Variant.replace(":id", variantId),
        }))
        .concat(sysEnggReports, projectReports);

      if (isSystemEngineer) {
        setAllReportsData(sysEnggReports);
      } else if (isRiskAnalyst) {
        const tmpData = variantId === COMMON.nullUuid ? riskAnalystReports : riskAnalystWithVariantReports;
        setAllReportsData(tmpData);
      }
    }
    if (systemData && variantId) {
      getData();
    }
  }, [isRiskAnalyst, isSystemEngineer, projectId, systemData, variantId]);

  if (isSystemError) {
    return <ErrorBanner msg="Error occured while getting Reports" />;
  }

  if (allReportsData) {
    return (
      <>
        {exportTraIsOpen &&
          systemModals.customModal(
            "TraExport_modal",
            exportTraIsOpen,
            setExportTraIsOpen,
            <ExportWizard
              filePrefix={currentProject?.name || "TRA"}
              setExportModalIsOpen={setExportTraIsOpen}
              projectId={projectId}
              exportContext={ExportContext.tra}
              focus={COMMON.nullUuid}
            />,
            "TRA Exporter"
          )}

        {exportRARIsOpen &&
          systemModals.customModal(
            "RARExport_modal",
            exportRARIsOpen,
            setExportRARIsOpen,
            <ExportWizard
              filePrefix={currentProject?.name || "RAR"}
              setExportModalIsOpen={setExportRARIsOpen}
              projectId={projectId}
              exportContext={ExportContext.rar}
              focus={COMMON.nullUuid}
              showCurrentOption={false}
            />,
            "RAR Exporter"
          )}

        {exportSCTMIsOpen &&
          systemModals.customModal(
            "SCTMExport_modal",
            exportSCTMIsOpen,
            setExportSCTMIsOpen,
            <ExportWizard
              filePrefix={currentProject?.name || "SCTM"}
              setExportModalIsOpen={setExportSCTMIsOpen}
              projectId={projectId}
              // sctmId={id}
              exportContext={ExportContext.sctm}
            />,
            "SCTM Exporter"
          )}

        <ExportTableModal
          onTableExport={handleExportTnEReport}
          isVisible={isTnEModalVisible}
          toggle={isTnEExportRequestSent.current ? toggleTnECloseModal : toggleTnECloseModalEnableButton}
          context={ExportContext.tne}
          exporterName="Test Guidance Exporter"
        />

        <CustomTable id="UserTable_table">
          <BrmMainTable
            data={allReportsData}
            columns={columns}
            setSelectedRows={() => {}}
            customProps={{ id: "UserTable_table" }}
            elementName={TYPE.user}
            showRowSelect={false}
          />
        </CustomTable>
      </>
    );
  }

  return <Loading />;
};
